import React, { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiImage } from "react-icons/fi";
import { typography, spacing, components } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { websiteTypes } from "../data/websiteTypes";

const firstRowTypes = websiteTypes.slice(0, 4);
const secondRowTypes = websiteTypes.slice(4);

const useNativeTouchCarousel = () => {
  const [useNativeTouch, setUseNativeTouch] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(hover: none) and (pointer: coarse)");

    const handleChange = (event) => {
      setUseNativeTouch(event.matches);
    };

    setUseNativeTouch(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return useNativeTouch;
};

const WebsiteTypeCard = memo(function WebsiteTypeCard({
  type,
  touchCarousel = false,
}) {
  const Icon = type.icon;

  return (
    <article
      className={`shrink-0 overflow-hidden rounded-[28px] border border-secondary/10 bg-[#fff7ec] p-4 text-secondary shadow-md ${
        touchCarousel
          ? "w-[84vw] max-w-[320px] snap-center"
          : "w-[280px] md:w-[320px]"
      }`}
    >
      <div className="relative h-36 overflow-hidden rounded-[22px] bg-secondary/10 md:h-40">
        {type.image ? (
          <img
            src={type.image}
            alt={type.title}
            loading="lazy"
            decoding="async"
            draggable="false"
            className="pointer-events-none h-full w-full select-none object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,rgba(72,103,38,0.16),rgba(230,126,34,0.14))]">
            <div className="flex flex-col items-center gap-2 text-secondary/50">
              <FiImage className="text-2xl" />

              <span className="text-xs font-semibold uppercase tracking-widest">
                Image placeholder
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="px-1 pb-1 pt-5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary">
            <Icon />
          </span>

          <p className={`${typography.label} text-primary`}>{type.label}</p>
        </div>

        <h3 className={`${typography.card.large} mt-4`}>{type.title}</h3>

        <p className={`${typography.body.small} mt-3`}>{type.text}</p>
      </div>
    </article>
  );
});

const TouchCarouselRow = memo(function TouchCarouselRow({ items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div
      className="website-types-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden px-5 pb-4 pt-2"
      style={{
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorX: "contain",
        scrollPaddingInline: "20px",
      }}
    >
      {items.map((type, index) => (
        <WebsiteTypeCard
          key={`${type.title}-${index}`}
          type={type}
          touchCarousel
        />
      ))}

      <div aria-hidden="true" className="w-1 shrink-0" />
    </div>
  );
});

const DesktopMarqueeRow = memo(function DesktopMarqueeRow({
  items,
  reverse = false,
  duration = 34,
}) {
  const viewportRef = useRef(null);
  const dragLayerRef = useRef(null);
  const trackRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startOffset: 0,
    currentOffset: 0,
  });

  const applyDragOffset = (offset) => {
    const dragLayer = dragLayerRef.current;

    if (!dragLayer) {
      return;
    }

    dragRef.current.currentOffset = offset;

    dragLayer.style.transform = `translate3d(${offset}px, 0, 0)`;
  };

  const normalizeDragOffset = () => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    /*
     * There are three identical copies inside the track.
     * One copy therefore occupies one third of its width.
     */
    const singleCopyWidth = track.scrollWidth / 3;

    if (!singleCopyWidth) {
      return;
    }

    const current = dragRef.current.currentOffset;

    let normalized =
      ((current % singleCopyWidth) + singleCopyWidth) % singleCopyWidth;

    if (normalized > singleCopyWidth / 2) {
      normalized -= singleCopyWidth;
    }

    applyDragOffset(normalized);
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!viewport || !track) {
      return;
    }

    event.preventDefault();

    dragRef.current.active = true;
    dragRef.current.pointerId = event.pointerId;
    dragRef.current.startX = event.clientX;
    dragRef.current.startOffset = dragRef.current.currentOffset;

    track.style.animationPlayState = "paused";

    viewport.setPointerCapture(event.pointerId);

    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;

    if (!drag.active || drag.pointerId !== event.pointerId) {
      return;
    }

    const movement = event.clientX - drag.startX;

    applyDragOffset(drag.startOffset + movement);
  };

  const stopDragging = (event) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const drag = dragRef.current;

    if (
      !viewport ||
      !track ||
      !drag.active ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    if (viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }

    drag.active = false;
    drag.pointerId = null;

    /*
     * Moving by one complete copy produces the
     * same visual result, so normalization creates
     * no visible jump.
     */
    normalizeDragOffset();

    track.style.animationPlayState = "running";

    setIsDragging(false);
  };

  if (!items.length) {
    return null;
  }

  return (
    <div
      ref={viewportRef}
      className={`relative overflow-hidden py-3 select-none ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={(event) => {
        if (dragRef.current.active) {
          stopDragging(event);
        }
      }}
      onDragStart={(event) => event.preventDefault()}
    >
      {/*
          Dragging controls this outer layer.
          Autoplay controls the inner track.
          They no longer overwrite each other's transform.
        */}
      <div
        ref={dragLayerRef}
        className="w-max"
        style={{
          transform: "translate3d(0, 0, 0)",
          willChange: isDragging ? "transform" : "auto",
        }}
      >
        <div
          ref={trackRef}
          className={`website-marquee-track flex w-max ${
            reverse ? "website-marquee-reverse" : ""
          }`}
          style={{
            "--duration": `${duration}s`,
          }}
        >
          {[0, 1, 2].map((groupIndex) => (
            <div
              key={groupIndex}
              aria-hidden={groupIndex !== 1}
              className="flex shrink-0 gap-5 pr-5"
            >
              {items.map((type, index) => (
                <WebsiteTypeCard
                  key={`${groupIndex}-${type.title}-${index}`}
                  type={type}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

const MarqueeRow = memo(function MarqueeRow({
  items,
  useNativeTouch,
  reverse = false,
  duration = 34,
}) {
  if (useNativeTouch) {
    return <TouchCarouselRow items={items} />;
  }

  return (
    <DesktopMarqueeRow items={items} reverse={reverse} duration={duration} />
  );
});

const WebsiteTypes = () => {
  const sectionRef = useRef(null);

  const useNativeTouch = useNativeTouchCarousel();

  const progress = useScrollProgress(sectionRef);

  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="website-types"
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-24 text-secondary md:py-40"
    >
      <style>
        {`
          @keyframes websiteTypesMarquee {
            from {
              transform: translate3d(0, 0, 0);
            }

            to {
              transform: translate3d(
                -33.333333%,
                0,
                0
              );
            }
          }

          .website-marquee-track {
            animation:
              websiteTypesMarquee
              var(--duration)
              linear
              infinite;

            will-change: transform;
            backface-visibility: hidden;
          }

          .website-marquee-reverse {
            animation-direction: reverse;
          }

          .website-types-scrollbar {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .website-types-scrollbar::-webkit-scrollbar {
            display: none;
          }

          @media (prefers-reduced-motion: reduce) {
            .website-marquee-track {
              animation: none;
            }
          }
        `}
      </style>

      <div className={`relative z-10 ${spacing.container}`}>
        <div className="space-y-10 md:space-y-12">
          <div
            className="max-w-3xl space-y-6"
            style={{
              opacity: visibility,
              transform: `translate3d(
                0,
                ${(1 - visibility) * 20}px,
                0
              )`,
            }}
          >
            <p className={`${typography.label} text-primary`}>
              What we can build
            </p>

            <h2 className={`${typography.heading.h2} text-secondary`}>
              The right website depends on what your business actually needs
            </h2>

            <div
              className="h-1 w-14 rounded-full bg-gradient-to-r from-primary to-secondary"
              style={{
                transform: `scaleX(${visibility})`,
                transformOrigin: "left",
              }}
            />
          </div>

          <div
            className="relative left-1/2 w-screen overflow-hidden bg-white"
            style={{
              opacity: visibility,
              transform: `translate3d(
                -50%,
                ${(1 - visibility) * 20}px,
                0
              )`,
            }}
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-20 bg-gradient-to-r from-white via-white/80 to-transparent md:block" />

            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-20 bg-gradient-to-l from-white via-white/80 to-transparent md:block" />

            <div className="py-1 md:py-2">
              <MarqueeRow
                items={firstRowTypes}
                useNativeTouch={useNativeTouch}
                duration={34}
              />

              <MarqueeRow
                items={secondRowTypes}
                useNativeTouch={useNativeTouch}
                reverse
                duration={38}
              />
            </div>
          </div>

          <div
            className="pt-2 md:pt-4"
            style={{
              opacity: Math.max(0, visibility * 1.4 - 0.4),
              transform: `translate3d(
                0,
                ${(1 - visibility) * 15}px,
                0
              )`,
            }}
          >
            <Link
              to="/contact"
              className={`${components.button.primary} bg-secondary text-sm`}
            >
              Discuss your website idea
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebsiteTypes;
